import { useState, useEffect, useRef, useCallback } from 'react';
import { PodcastEpisode, EpisodeStatus } from '@/types/episode.types';
import { Clip, ClipStatus } from '@/types/clip.types';
import { useWebSocket, EpisodeUpdate, ClipUpdate } from '@/contexts/WebSocketContext';
import { customToast } from '@/components/ui/custom-toast';
import { showCelebrationToast } from '@/components/ui/celebration-toast';
import { playNotificationSound } from '@/utils/sounds';
import { normalizeStatus } from '@/utils/episode-helpers';
// @ts-ignore - Module Federation dynamic import
import { auth } from 'shell/Firebase';
import type { UserPresence } from '@/types/presence.types';

interface UseEpisodeRealtimeArgs {
  episodeId: string | undefined;
  setEpisode: React.Dispatch<React.SetStateAction<PodcastEpisode | null>>;
  setClips: React.Dispatch<React.SetStateAction<Clip[]>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  loadClips: () => void;
  episodeTitle: string | undefined;
}

export function useEpisodeRealtime({
  episodeId,
  setEpisode,
  setClips,
  setError,
  loadClips,
  episodeTitle,
}: UseEpisodeRealtimeArgs) {
  const {
    isConnected,
    onEpisodeUpdate,
    onClipUpdate,
    subscribeToEpisode,
    unsubscribeFromEpisode,
    joinPresence,
    leavePresence,
    onPresenceUpdate,
  } = useWebSocket();

  // Presence state
  const [activeViewers, setActiveViewers] = useState<UserPresence[]>([]);

  // Lock state
  const isLockedByOther = activeViewers.length > 0 &&
    activeViewers[0]?.odId !== auth.currentUser?.uid;
  const lockHolder = isLockedByOther ? activeViewers[0] : null;

  // Lock state change counter — increments when lock transitions to locked
  const [lockStateChanged, setLockStateChanged] = useState(0);
  const wasLockedByOtherRef = useRef(isLockedByOther);

  // Track previous status for toast notifications
  const previousStatusRef = useRef<EpisodeStatus | null>(null);
  const isInitialLoadRef = useRef(true);

  // Subscribe to episode WebSocket updates
  useEffect(() => {
    if (episodeId && isConnected) {
      subscribeToEpisode(episodeId);
      return () => { unsubscribeFromEpisode(episodeId); };
    }
  }, [episodeId, isConnected, subscribeToEpisode, unsubscribeFromEpisode]);

  // Join presence
  useEffect(() => {
    if (episodeId && isConnected) {
      joinPresence(episodeId);
      return () => { leavePresence(episodeId); };
    }
  }, [episodeId, isConnected, joinPresence, leavePresence]);

  // Listen for presence updates
  useEffect(() => {
    const unsubscribe = onPresenceUpdate((update) => {
      if (update.episodeId === episodeId) {
        setActiveViewers(update.viewers);
      }
    });
    return unsubscribe;
  }, [episodeId, onPresenceUpdate]);

  // Detect lock state transitions
  useEffect(() => {
    if (isLockedByOther && !wasLockedByOtherRef.current && lockHolder) {
      setLockStateChanged((c) => c + 1);
      customToast.warning(`This episode is now being edited by ${lockHolder.displayName}`, {
        description: 'Your changes may be overwritten. Wait until they are done editing.',
      });
    }
    wasLockedByOtherRef.current = isLockedByOther;
  }, [isLockedByOther, lockHolder]);

  // Handle episode updates
  useEffect(() => {
    const handleUpdate = (update: EpisodeUpdate) => {
      if (update.id !== episodeId) return;

      const previousStatus = previousStatusRef.current;
      const newStatus = normalizeStatus(update.status);

      // Show toast notification if status changed (but not on initial load)
      if (!isInitialLoadRef.current && previousStatus && previousStatus !== newStatus) {
        const statusMessages: Partial<Record<EpisodeStatus, string>> = {
          [EpisodeStatus.DRAFT]: 'Episode created as draft',
          [EpisodeStatus.GENERATING_SCRIPTS]: 'Generating scripts...',
          [EpisodeStatus.SCRIPTS_PENDING_REVIEW]: 'All scripts ready for review',
          [EpisodeStatus.SCRIPTS_APPROVED]: 'Scripts approved!',
          [EpisodeStatus.AUDIO_PENDING_REVIEW]: 'All audio ready for review',
          [EpisodeStatus.AUDIO_APPROVED]: 'Audio approved!',
          [EpisodeStatus.MERGING]: 'Merging clips...',
          [EpisodeStatus.MERGED]: 'Episode merged successfully!',
          [EpisodeStatus.FAILED]: 'Episode failed',
          [EpisodeStatus.PUBLISHING]: 'Publishing...',
          [EpisodeStatus.PUBLISHED]: 'Episode published successfully!',
          [EpisodeStatus.PUBLISHING_FAILED]: 'Publishing failed',
        };

        const isErrorStatus = newStatus.includes('failed');

        if (newStatus === EpisodeStatus.PUBLISHED) {
          showCelebrationToast({
            title: 'Episode Published!',
            description: episodeTitle || 'Your episode is now live',
            withConfetti: true,
          });
        } else {
          if (newStatus === EpisodeStatus.SCRIPTS_PENDING_REVIEW ||
              newStatus === EpisodeStatus.AUDIO_PENDING_REVIEW ||
              newStatus === EpisodeStatus.MERGED) {
            playNotificationSound();
          }

          const toastFn = isErrorStatus ? customToast.error : customToast.success;
          toastFn(statusMessages[newStatus] || 'Episode updated', {
            description: `Status changed to ${newStatus.replace(/_/g, ' ')}`,
          });
        }
      }

      previousStatusRef.current = newStatus;
      isInitialLoadRef.current = false;

      setEpisode((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          status: newStatus,
          title: update.title || prev.title,
          updatedAt: new Date(update.updatedAt),
          audioUrl: (update as any).audioUrl || prev.audioUrl,
          mergedAudioPath: (update as any).mergedAudioPath || prev.mergedAudioPath,
          coverArtUrl: (update as any).coverArtUrl ?? prev.coverArtUrl,
          scheduledPublishDate: (update as any).scheduledPublishDate ?? prev.scheduledPublishDate,
          publishedAt: (update as any).publishedAt ?? prev.publishedAt,
          publishingProviderUrl: (update as any).publishingProviderUrl ?? (update as any).playerUrl ?? prev.publishingProviderUrl,
          suggestedTitles: (update as any).suggestedTitles ?? prev.suggestedTitles,
          suggestedDescriptions: (update as any).suggestedDescriptions ?? prev.suggestedDescriptions,
          suggestionsStatus: (update as any).suggestionsStatus ?? prev.suggestionsStatus,
          suggestionsError: (update as any).suggestionsError ?? prev.suggestionsError,
        };
      });
      setError(null);
      loadClips();
    };

    const unsubscribe = onEpisodeUpdate(handleUpdate);
    return unsubscribe;
  }, [episodeId, onEpisodeUpdate, loadClips, episodeTitle, setEpisode, setError]);

  // Handle clip updates
  useEffect(() => {
    const handleClipUpdate = (update: ClipUpdate) => {
      if (update.episodeId !== episodeId) return;

      setClips((prev) =>
        prev.map((clip) =>
          clip.id === update.id
            ? {
                ...clip,
                status: update.status as ClipStatus,
                script: update.script !== undefined ? update.script : clip.script,
                audioPath: update.audioPath !== undefined ? update.audioPath : clip.audioPath,
                updatedAt: update.updatedAt,
              }
            : clip
        )
      );
      loadClips();
    };

    const unsubscribe = onClipUpdate(handleClipUpdate);
    return unsubscribe;
  }, [episodeId, onClipUpdate, loadClips, setClips]);

  /** Call this after initial episode load to set the tracking ref */
  const setInitialStatus = useCallback((status: EpisodeStatus) => {
    previousStatusRef.current = status;
  }, []);

  return {
    activeViewers,
    isLockedByOther,
    lockHolder,
    lockStateChanged,
    setInitialStatus,
  };
}
